import api from './api'

// Types
export interface OutgoingWebhook {
  id: string
  name: string
  url: string
  events: string[]
  events_display: string[]
  available_events: [string, string][]
  headers: Record<string, string>
  secret?: string
  is_active: boolean
  retry_count: number
  timeout_seconds: number
  last_triggered_at?: string
  success_count: number
  failure_count: number
  created_at: string
  updated_at: string
}

export interface WebhookDelivery {
  id: string
  webhook: string
  webhook_name: string
  event: string
  payload: Record<string, any>
  status: 'pending' | 'success' | 'failed' | 'retrying'
  status_display: string
  response_status_code?: number
  response_body?: string
  error_message?: string
  attempt_count: number
  delivered_at?: string
  duration_ms?: number
  created_at: string
}

export interface EventOption {
  value: string
  label: string
}

export interface CreateOutgoingWebhook {
  name: string
  url: string
  events: string[]
  headers?: Record<string, string>
  secret?: string
  is_active?: boolean
  retry_count?: number
  timeout_seconds?: number
}

export interface TestWebhookResult {
  success: boolean
  status_code?: number
  message: string
}

// API functions
export const outgoingWebhooksApi = {
  // List all outgoing webhooks
  list: async (params?: { is_active?: boolean; search?: string }): Promise<OutgoingWebhook[]> => {
    const response = await api.get('/integrations/outgoing-webhooks/', { params })
    return response.data.results || response.data
  },

  // Get single webhook
  get: async (id: string): Promise<OutgoingWebhook> => {
    const response = await api.get(`/integrations/outgoing-webhooks/${id}/`)
    return response.data
  },

  // Create new webhook
  create: async (data: CreateOutgoingWebhook): Promise<OutgoingWebhook> => {
    const response = await api.post('/integrations/outgoing-webhooks/', data)
    return response.data
  },

  // Update webhook
  update: async (id: string, data: Partial<CreateOutgoingWebhook>): Promise<OutgoingWebhook> => {
    const response = await api.patch(`/integrations/outgoing-webhooks/${id}/`, data)
    return response.data
  },

  // Delete webhook
  delete: async (id: string): Promise<void> => {
    await api.delete(`/integrations/outgoing-webhooks/${id}/`)
  },

  // Toggle active status
  toggleActive: async (id: string): Promise<{ success: boolean; is_active: boolean; message: string }> => {
    const response = await api.post(`/integrations/outgoing-webhooks/${id}/toggle_active/`)
    return response.data
  },

  // Test webhook
  test: async (id: string): Promise<TestWebhookResult> => {
    const response = await api.post(`/integrations/outgoing-webhooks/${id}/test/`)
    return response.data
  },

  // Get available events
  getAvailableEvents: async (): Promise<EventOption[]> => {
    const response = await api.get('/integrations/outgoing-webhooks/available_events/')
    return response.data
  },

  // Get webhook deliveries
  getDeliveries: async (params?: {
    webhook?: string
    event?: string
    status?: string
  }): Promise<WebhookDelivery[]> => {
    const response = await api.get('/integrations/webhook-deliveries/', { params })
    return response.data.results || response.data
  },

  // Retry failed delivery
  retryDelivery: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/integrations/webhook-deliveries/${id}/retry/`)
    return response.data
  },
}

export default outgoingWebhooksApi
